import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Paper, Radio, RadioGroup,
  FormControlLabel, Button, Box, CircularProgress, FormControl,useMediaQuery
} from '@mui/material';
import MonthSelectorDropdown from '../../MonthSelectorDropdown';
import * as XLSX from 'xlsx';
import { useSalesContext } from '../../SalesContext';
import ArrowUpwardIcon from "../../../assets/images/ArrowUpwardIcon_green.svg";
import ArrowDownwardIcon from "../../../assets/images/ArrowDownwardIcon_red.svg";

interface FiscalYearData {
  FY2023: string | number;
  FY2024: string | number;
  FY2025: string | number;
}

interface MonthData {
  Net_Sales: FiscalYearData;
  Net_Sales_Qty: FiscalYearData;
  Growth: FiscalYearData;
  Growth_Qty: FiscalYearData;
  PCPM: FiscalYearData;
  PCPM_Qty: FiscalYearData;
  Per_Ach: FiscalYearData;
  Per_Ach_Qty: FiscalYearData;
  [key: string]: any;
}

interface QuarterData {
  Months: {
    [month: string]: MonthData;
    Total: MonthData;
  };
}

interface GrandTotal {
  Net_Sales: FiscalYearData;
  Net_Sales_Qty: FiscalYearData;
  Growth: FiscalYearData;
  Growth_Qty: FiscalYearData;
  PCPM: FiscalYearData;
  PCPM_Qty: FiscalYearData;
  Per_Ach: FiscalYearData;
  Per_Ach_Qty: FiscalYearData;
  [key: string]: any;
}

interface SKU {
  Name: string;
  Months: { [key: string]: MonthData };
  Quarters: { [key: string]: QuarterData };
  GrandTotal: GrandTotal;
  Balance: number;
  Balance_Qty: number;
}

interface BrandData {
  Name: string;
  Brands_Months: { [key: string]: MonthData };
  Brands_Quarters: { [key: string]: QuarterData };
  Brands_GrandTotal: GrandTotal;
  Brands_Balance: number;
  Brands_Balance_Qty: number;
  SKUs: SKU[];
}

interface BrandFamilyData {
  BrandFamily: string;
  BrandFamily_Months: { [key: string]: MonthData };
  BrandFamily_Quarters: { [key: string]: QuarterData };
  BrandFamily_GrandTotal: GrandTotal;
  BrandFamily_Balance: number;
  BrandFamily_Balance_Qty: number;
  Brands: BrandData[];
}

const defaultGrandTotal: GrandTotal = {
  Net_Sales: { FY2023: 0, FY2024: 0, FY2025: 0 },
  Net_Sales_Qty: { FY2023: 0, FY2024: 0, FY2025: 0 },
  Growth: { FY2023: 0, FY2024: 0, FY2025: 0 },
  Growth_Qty: { FY2023: 0, FY2024: 0, FY2025: 0 },
  PCPM: { FY2023: 0, FY2024: 0, FY2025: 0 },
  PCPM_Qty: { FY2023: 0, FY2024: 0, FY2025: 0 },
  Per_Ach: { FY2023: 0, FY2024: 0, FY2025: 0 },
  Per_Ach_Qty: { FY2023: 0, FY2024: 0, FY2025: 0 },
};

const adjustValueByUnit = (value: string | number, salesUnit: string): number => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 0;
  if (salesUnit === 'Lacs') {
    return Number((numValue / 100000).toFixed(1));
  } else if (salesUnit === 'Crores') {
    return Number((numValue / 10000000).toFixed(1));
  }
  return Number(numValue.toFixed(1));
};


const ensureCompleteData = (sku: Partial<SKU>, salesUnit: string): SKU => {
  const allMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March',
  ];
  const allQuarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const quarterMonths: { [key: string]: string[] } = {
    Q1: ['April', 'May', 'June'],
    Q2: ['July', 'August', 'September'],
    Q3: ['October', 'November', 'December'],
    Q4: ['January', 'February', 'March'],
  };

  const completeMonths = allMonths.reduce((acc, month) => {
    const monthData = sku.Months?.[month] || {};
    return {
      ...acc,
      [month]: {
        Net_Sales: {
          FY2023: adjustValueByUnit(monthData.Net_Sales?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(monthData.Net_Sales?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(monthData.Net_Sales?.FY2025 || 0, salesUnit),
        },
        Net_Sales_Qty: {
          FY2023:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2023 || 0,salesUnit),
          FY2024:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2024 || 0,salesUnit),
          FY2025:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2025 || 0,salesUnit),
        },
        Growth: {
          FY2023: monthData.Growth?.FY2023 || 0,
          FY2024: monthData.Growth?.FY2024 || 0,
          FY2025: monthData.Growth?.FY2025 || 0,
        },
        Growth_Qty: {
          FY2023: monthData.Growth_Qty?.FY2023 || 0,
          FY2024: monthData.Growth_Qty?.FY2024 || 0,
          FY2025: monthData.Growth_Qty?.FY2025 || 0,
        },
        PCPM: {
          FY2023: adjustValueByUnit(monthData.PCPM?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(monthData.PCPM?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(monthData.PCPM?.FY2025 || 0, salesUnit),
        },
        PCPM_Qty: {
          FY2023:adjustValueByUnit( monthData.PCPM_Qty?.FY2023 || 0,salesUnit),
          FY2024: adjustValueByUnit(monthData.PCPM_Qty?.FY2024 || 0,salesUnit),
          FY2025: adjustValueByUnit(monthData.PCPM_Qty?.FY2025 || 0,salesUnit),
        },
        Per_Ach: {
          FY2023: monthData.Per_Ach?.FY2023 || 0,
          FY2024: monthData.Per_Ach?.FY2024 || 0,
          FY2025: monthData.Per_Ach?.FY2025 || 0,
        },
        Per_Ach_Qty: {
          FY2023: monthData.Per_Ach_Qty?.FY2023 || 0,
          FY2024: monthData.Per_Ach_Qty?.FY2024 || 0,
          FY2025: monthData.Per_Ach_Qty?.FY2025 || 0,
        },
      },
    };
  }, {});

  const completeQuarters = allQuarters.reduce((acc, quarter) => {
    const quarterData = sku.Quarters?.[quarter] || {};
    const months = quarterMonths[quarter].reduce((monthAcc, month) => {
      const monthData = sku.Months?.[month] || {};
      return {
        ...monthAcc,
        [month]: {
          Net_Sales: {
            FY2023: adjustValueByUnit(monthData.Net_Sales?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(monthData.Net_Sales?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.Net_Sales?.FY2025 || 0, salesUnit),
          },
          Net_Sales_Qty: {
            FY2023: adjustValueByUnit(monthData.Net_Sales_Qty?.FY2023 || 0,salesUnit),
            FY2024: adjustValueByUnit(monthData.Net_Sales_Qty?.FY2024 || 0,salesUnit),
            FY2025: adjustValueByUnit(monthData.Net_Sales_Qty?.FY2025 || 0,salesUnit),
          },
          Growth: {
            FY2023: monthData.Growth?.FY2023 || 0,
            FY2024: monthData.Growth?.FY2024 || 0,
            FY2025: monthData.Growth?.FY2025 || 0,
          },
          Growth_Qty: {
            FY2023: monthData.Growth_Qty?.FY2023 || 0,
            FY2024: monthData.Growth_Qty?.FY2024 || 0,
            FY2025: monthData.Growth_Qty?.FY2025 || 0,
          },
          PCPM: {
            FY2023: adjustValueByUnit(monthData.PCPM?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(monthData.PCPM?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.PCPM?.FY2025 || 0, salesUnit),
          },
          PCPM_Qty: {
            FY2023: adjustValueByUnit(monthData.PCPM_Qty?.FY2023 || 0,salesUnit),
            FY2024:adjustValueByUnit( monthData.PCPM_Qty?.FY2024 || 0,salesUnit),
            FY2025:adjustValueByUnit( monthData.PCPM_Qty?.FY2025 || 0,salesUnit),
          },
          Per_Ach: {
            FY2023: monthData.Per_Ach?.FY2023 || 0,
            FY2024: monthData.Per_Ach?.FY2024 || 0,
            FY2025: monthData.Per_Ach?.FY2025 || 0,
          },
          Per_Ach_Qty: {
            FY2023: monthData.Per_Ach_Qty?.FY2023 || 0,
            FY2024: monthData.Per_Ach_Qty?.FY2024 || 0,
            FY2025: monthData.Per_Ach_Qty?.FY2025 || 0,
          },
        },
      };
    }, {});

    const totalData = {
      Net_Sales: {
        FY2023: adjustValueByUnit(quarterData.Net_Sales?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(quarterData.Net_Sales?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(quarterData.Net_Sales?.FY2025 || 0, salesUnit),
      },
      Net_Sales_Qty: {
        FY2023: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2023 || 0,salesUnit),
        FY2024: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2024 || 0,salesUnit),
        FY2025: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2025 || 0,salesUnit),
      },
      Growth: {
        FY2023: quarterData.Growth?.FY2023 || 0,
        FY2024: quarterData.Growth?.FY2024 || 0,
        FY2025: quarterData.Growth?.FY2025 || 0,
      },
      Growth_Qty: {
        FY2023: quarterData.Growth_Qty?.FY2023 || 0,
        FY2024: quarterData.Growth_Qty?.FY2024 || 0,
        FY2025: quarterData.Growth_Qty?.FY2025 || 0,
      },
      PCPM: {
        FY2023: adjustValueByUnit(quarterData.PCPM?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(quarterData.PCPM?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(quarterData.PCPM?.FY2025 || 0, salesUnit),
      },
      PCPM_Qty: {
        FY2023: adjustValueByUnit(quarterData.PCPM_Qty?.FY2023 || 0,salesUnit),
        FY2024: adjustValueByUnit(quarterData.PCPM_Qty?.FY2024 || 0,salesUnit),
        FY2025: adjustValueByUnit(quarterData.PCPM_Qty?.FY2025 || 0,salesUnit),
      },
      Per_Ach: {
        FY2023: quarterData.Per_Ach?.FY2023 || 0,
        FY2024: quarterData.Per_Ach?.FY2024 || 0,
        FY2025: quarterData.Per_Ach?.FY2025 || 0,
      },
      Per_Ach_Qty: {
        FY2023: quarterData.Per_Ach_Qty?.FY2023 || 0,
        FY2024: quarterData.Per_Ach_Qty?.FY2024 || 0,
        FY2025: quarterData.Per_Ach_Qty?.FY2025 || 0,
      },
    };

    return {
      ...acc,
      [quarter]: {
        Months: {
          ...months,
          Total: totalData,
        },
      },
    };
  }, {});

  const grandTotalData = sku.GrandTotal || defaultGrandTotal;
  return {
    Name: sku.Name || 'Unnamed SKU',
    Months: completeMonths,
    Quarters: completeQuarters,
    GrandTotal: {
      Net_Sales: {
        FY2023: adjustValueByUnit(grandTotalData.Net_Sales?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(grandTotalData.Net_Sales?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(grandTotalData.Net_Sales?.FY2025 || 0, salesUnit),
      },
      Net_Sales_Qty: {
        FY2023: adjustValueByUnit(grandTotalData.Net_Sales_Qty?.FY2023 || 0,salesUnit),
        FY2024:adjustValueByUnit( grandTotalData.Net_Sales_Qty?.FY2024 || 0,salesUnit),
        FY2025: adjustValueByUnit(grandTotalData.Net_Sales_Qty?.FY2025 || 0,salesUnit),
      },
      Growth: {
        FY2023: grandTotalData.Growth?.FY2023 || 0,
        FY2024: grandTotalData.Growth?.FY2024 || 0,
        FY2025: grandTotalData.Growth?.FY2025 || 0,
      },
      Growth_Qty: {
        FY2023: grandTotalData.Growth_Qty?.FY2023 || 0,
        FY2024: grandTotalData.Growth_Qty?.FY2024 || 0,
        FY2025: grandTotalData.Growth_Qty?.FY2025 || 0,
      },
      PCPM: {
        FY2023: adjustValueByUnit(grandTotalData.PCPM?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(grandTotalData.PCPM?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(grandTotalData.PCPM?.FY2025 || 0, salesUnit),
      },
      PCPM_Qty: {
        FY2023: adjustValueByUnit(grandTotalData.PCPM_Qty?.FY2023 || 0,salesUnit),
        FY2024: adjustValueByUnit(grandTotalData.PCPM_Qty?.FY2024 || 0,salesUnit),
        FY2025: adjustValueByUnit(grandTotalData.PCPM_Qty?.FY2025 || 0,salesUnit),
      },
      Per_Ach: {
        FY2023: grandTotalData.Per_Ach?.FY2023 || 0,
        FY2024: grandTotalData.Per_Ach?.FY2024 || 0,
        FY2025: grandTotalData.Per_Ach?.FY2025 || 0,
      },
      Per_Ach_Qty: {
        FY2023: grandTotalData.Per_Ach_Qty?.FY2023 || 0,
        FY2024: grandTotalData.Per_Ach_Qty?.FY2024 || 0,
        FY2025: grandTotalData.Per_Ach_Qty?.FY2025 || 0,
      },
    },
    Balance: adjustValueByUnit(sku.Balance || 0, salesUnit),
    Balance_Qty: adjustValueByUnit(sku.Balance_Qty || 0,salesUnit),
  };
};

const normalizeBrandAndFamilyData = (data: BrandFamilyData[], salesUnit: string): BrandFamilyData[] => {
  const allMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March',
  ];
  const allQuarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const quarterMonths: { [key: string]: string[] } = {
    Q1: ['April', 'May', 'June'],
    Q2: ['July', 'August', 'September'],
    Q3: ['October', 'November', 'December'],
    Q4: ['January', 'February', 'March'],
  };

  return data.map((brandFamily) => {
    const brandFamilyMonths = allMonths.reduce((acc, month) => {
      const monthData = brandFamily.BrandFamily_Months?.[month] || {};
      return {
        ...acc,
        [month]: {
          Net_Sales: {
            FY2023: adjustValueByUnit(monthData.Net_Sales?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(monthData.Net_Sales?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.Net_Sales?.FY2025 || 0, salesUnit),
          },
          Net_Sales_Qty: {
            FY2023: adjustValueByUnit(monthData.Net_Sales_Qty?.FY2023 || 0, salesUnit),
            FY2024:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.Net_Sales_Qty?.FY2025 || 0, salesUnit),
          },
          Growth: {
            FY2023: monthData.Growth?.FY2023 || 0,
            FY2024: monthData.Growth?.FY2024 || 0,
            FY2025: monthData.Growth?.FY2025 || 0,
          },
          Growth_Qty: {
            FY2023: monthData.Growth_Qty?.FY2023 || 0,
            FY2024: monthData.Growth_Qty?.FY2024 || 0,
            FY2025: monthData.Growth_Qty?.FY2025 || 0,
          },
          PCPM: {
            FY2023: adjustValueByUnit(monthData.PCPM?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(monthData.PCPM?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.PCPM?.FY2025 || 0, salesUnit),
          },
          PCPM_Qty: {
            FY2023: adjustValueByUnit(monthData.PCPM_Qty?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(monthData.PCPM_Qty?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(monthData.PCPM_Qty?.FY2025 || 0, salesUnit),
          },
          Per_Ach: {
            FY2023: monthData.Per_Ach?.FY2023 || 0,
            FY2024: monthData.Per_Ach?.FY2024 || 0,
            FY2025: monthData.Per_Ach?.FY2025 || 0,
          },
          Per_Ach_Qty: {
            FY2023: monthData.Per_Ach_Qty?.FY2023 || 0,
            FY2024: monthData.Per_Ach_Qty?.FY2024 || 0,
            FY2025: monthData.Per_Ach_Qty?.FY2025 || 0,
          },
        },
      };
    }, {});

    const brandFamilyQuarters = allQuarters.reduce((acc, quarter) => {
      const quarterData = brandFamily.BrandFamily_Quarters?.[quarter] || {};
      const months = quarterMonths[quarter].reduce((monthAcc, month) => {
        const monthData = brandFamilyMonths[month] || {};
        return {
          ...monthAcc,
          [month]: monthData,
        };
      }, {});

      const totalData = {
        Net_Sales: {
          FY2023: adjustValueByUnit(quarterData.Net_Sales?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(quarterData.Net_Sales?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(quarterData.Net_Sales?.FY2025 || 0, salesUnit),
        },
        Net_Sales_Qty: {
          FY2023: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2023 || 0,salesUnit),
          FY2024: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2024 || 0,salesUnit),
          FY2025: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2025 || 0,salesUnit),
        },
        Growth: {
          FY2023: quarterData.Growth?.FY2023 || 0,
          FY2024: quarterData.Growth?.FY2024 || 0,
          FY2025: quarterData.Growth?.FY2025 || 0,
        },
        Growth_Qty: {
          FY2023: quarterData.Growth_Qty?.FY2023 || 0,
          FY2024: quarterData.Growth_Qty?.FY2024 || 0,
          FY2025: quarterData.Growth_Qty?.FY2025 || 0,
        },
        PCPM: {
          FY2023: adjustValueByUnit(quarterData.PCPM?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(quarterData.PCPM?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(quarterData.PCPM?.FY2025 || 0, salesUnit),
        },
        PCPM_Qty: {
          FY2023: adjustValueByUnit(quarterData.PCPM_Qty?.FY2023 || 0,salesUnit),
          FY2024: adjustValueByUnit(quarterData.PCPM_Qty?.FY2024 || 0,salesUnit),
          FY2025: adjustValueByUnit(quarterData.PCPM_Qty?.FY2025 || 0,salesUnit),
        },
        Per_Ach: {
          FY2023: quarterData.Per_Ach?.FY2023 || 0,
          FY2024: quarterData.Per_Ach?.FY2024 || 0,
          FY2025: quarterData.Per_Ach?.FY2025 || 0,
        },
        Per_Ach_Qty: {
          FY2023: quarterData.Per_Ach_Qty?.FY2023 || 0,
          FY2024: quarterData.Per_Ach_Qty?.FY2024 || 0,
          FY2025: quarterData.Per_Ach_Qty?.FY2025 || 0,
        },
      };

      return {
        ...acc,
        [quarter]: {
          Months: {
            ...months,
            Total: totalData,
          },
        },
      };
    }, {});

    const brandFamilyGrandTotal = {
      Net_Sales: {
        FY2023: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.Net_Sales?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.Net_Sales?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.Net_Sales?.FY2025 || 0, salesUnit),
      },
      Net_Sales_Qty: {
        FY2023: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.Net_Sales_Qty?.FY2023 || 0, salesUnit),
        FY2024:adjustValueByUnit( brandFamily.BrandFamily_GrandTotal?.Net_Sales_Qty?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.Net_Sales_Qty?.FY2025 || 0, salesUnit),
      },
      Growth: {
        FY2023: brandFamily.BrandFamily_GrandTotal?.Growth?.FY2023 || 0,
        FY2024: brandFamily.BrandFamily_GrandTotal?.Growth?.FY2024 || 0,
        FY2025: brandFamily.BrandFamily_GrandTotal?.Growth?.FY2025 || 0,
      },
      Growth_Qty: {
        FY2023: brandFamily.BrandFamily_GrandTotal?.Growth_Qty?.FY2023 || 0,
        FY2024: brandFamily.BrandFamily_GrandTotal?.Growth_Qty?.FY2024 || 0,
        FY2025: brandFamily.BrandFamily_GrandTotal?.Growth_Qty?.FY2025 || 0,
      },
      PCPM: {
        FY2023: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM?.FY2025 || 0, salesUnit),
      },
      PCPM_Qty: {
        FY2023: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM_Qty?.FY2023 || 0, salesUnit),
        FY2024: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM_Qty?.FY2024 || 0, salesUnit),
        FY2025: adjustValueByUnit(brandFamily.BrandFamily_GrandTotal?.PCPM_Qty?.FY2025 || 0, salesUnit),
      },
      Per_Ach: {
        FY2023: brandFamily.BrandFamily_GrandTotal?.Per_Ach?.FY2023 || 0,
        FY2024: brandFamily.BrandFamily_GrandTotal?.Per_Ach?.FY2024 || 0,
        FY2025: brandFamily.BrandFamily_GrandTotal?.Per_Ach?.FY2025 || 0,
      },
      Per_Ach_Qty: {
        FY2023: brandFamily.BrandFamily_GrandTotal?.Per_Ach_Qty?.FY2023 || 0,
        FY2024: brandFamily.BrandFamily_GrandTotal?.Per_Ach_Qty?.FY2024 || 0,
        FY2025: brandFamily.BrandFamily_GrandTotal?.Per_Ach_Qty?.FY2025 || 0,
      },
    };

    const normalizedBrands = brandFamily.Brands.map((brand) => {
      const brandMonths = allMonths.reduce((acc, month) => {
        const monthData = brand.Brands_Months?.[month] || {};
        return {
          ...acc,
          [month]: {
            Net_Sales: {
              FY2023: adjustValueByUnit(monthData.Net_Sales?.FY2023 || 0, salesUnit),
              FY2024: adjustValueByUnit(monthData.Net_Sales?.FY2024 || 0, salesUnit),
              FY2025: adjustValueByUnit(monthData.Net_Sales?.FY2025 || 0, salesUnit),
            },
            Net_Sales_Qty: {
              FY2023:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2023 || 0, salesUnit),
              FY2024:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2024 || 0, salesUnit),
              FY2025:adjustValueByUnit( monthData.Net_Sales_Qty?.FY2025 || 0, salesUnit),
            },
            Growth: {
              FY2023: monthData.Growth?.FY2023 || 0,
              FY2024: monthData.Growth?.FY2024 || 0,
              FY2025: monthData.Growth?.FY2025 || 0,
            },
            Growth_Qty: {
              FY2023: monthData.Growth_Qty?.FY2023 || 0,
              FY2024: monthData.Growth_Qty?.FY2024 || 0,
              FY2025: monthData.Growth_Qty?.FY2025 || 0,
            },
            PCPM: {
              FY2023: adjustValueByUnit(monthData.PCPM?.FY2023 || 0, salesUnit),
              FY2024: adjustValueByUnit(monthData.PCPM?.FY2024 || 0, salesUnit),
              FY2025: adjustValueByUnit(monthData.PCPM?.FY2025 || 0, salesUnit),
            },
            PCPM_Qty: {
              FY2023:adjustValueByUnit( monthData.PCPM_Qty?.FY2023 || 0,salesUnit),
              FY2024:adjustValueByUnit( monthData.PCPM_Qty?.FY2024 || 0,salesUnit),
              FY2025:adjustValueByUnit( monthData.PCPM_Qty?.FY2025 || 0,salesUnit),
            },
            Per_Ach: {
              FY2023: monthData.Per_Ach?.FY2023 || 0,
              FY2024: monthData.Per_Ach?.FY2024 || 0,
              FY2025: monthData.Per_Ach?.FY2025 || 0,
            },
            Per_Ach_Qty: {
              FY2023: monthData.Per_Ach_Qty?.FY2023 || 0,
              FY2024: monthData.Per_Ach_Qty?.FY2024 || 0,
              FY2025: monthData.Per_Ach_Qty?.FY2025 || 0,
            },
          },
        };
      }, {});

      const brandQuarters = allQuarters.reduce((acc, quarter) => {
        const quarterData = brand.Brands_Quarters?.[quarter] || {};
        const months = quarterMonths[quarter].reduce((monthAcc, month) => {
          const monthData = brandMonths[month] || {};
          return {
            ...monthAcc,
            [month]: monthData,
          };
        }, {});

        const totalData = {
          Net_Sales: {
            FY2023: adjustValueByUnit(quarterData.Net_Sales?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(quarterData.Net_Sales?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(quarterData.Net_Sales?.FY2025 || 0, salesUnit),
          },
          Net_Sales_Qty: {
            FY2023: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(quarterData.Net_Sales_Qty?.FY2025 || 0, salesUnit),
          },
          Growth: {
            FY2023: quarterData.Growth?.FY2023 || 0,
            FY2024: quarterData.Growth?.FY2024 || 0,
            FY2025: quarterData.Growth?.FY2025 || 0,
          },
          Growth_Qty: {
            FY2023: quarterData.Growth_Qty?.FY2023 || 0,
            FY2024: quarterData.Growth_Qty?.FY2024 || 0,
            FY2025: quarterData.Growth_Qty?.FY2025 || 0,
          },
          PCPM: {
            FY2023: adjustValueByUnit(quarterData.PCPM?.FY2023 || 0, salesUnit),
            FY2024: adjustValueByUnit(quarterData.PCPM?.FY2024 || 0, salesUnit),
            FY2025: adjustValueByUnit(quarterData.PCPM?.FY2025 || 0, salesUnit),
          },
          PCPM_Qty: {
            FY2023: adjustValueByUnit(quarterData.PCPM_Qty?.FY2023 || 0,salesUnit),
            FY2024: adjustValueByUnit(quarterData.PCPM_Qty?.FY2024 || 0,salesUnit),
            FY2025: adjustValueByUnit(quarterData.PCPM_Qty?.FY2025 || 0,salesUnit),
          },
          Per_Ach: {
            FY2023: quarterData.Per_Ach?.FY2023 || 0,
            FY2024: quarterData.Per_Ach?.FY2024 || 0,
            FY2025: quarterData.Per_Ach?.FY2025 || 0,
          },
          Per_Ach_Qty: {
            FY2023: quarterData.Per_Ach_Qty?.FY2023 || 0,
            FY2024: quarterData.Per_Ach_Qty?.FY2024 || 0,
            FY2025: quarterData.Per_Ach_Qty?.FY2025 || 0,
          },
        };

        return {
          ...acc,
          [quarter]: {
            Months: {
              ...months,
              Total: totalData,
            },
          },
        };
      }, {});

      const brandGrandTotal = {
        Net_Sales: {
          FY2023: adjustValueByUnit(brand.Brands_GrandTotal?.Net_Sales?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(brand.Brands_GrandTotal?.Net_Sales?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(brand.Brands_GrandTotal?.Net_Sales?.FY2025 || 0, salesUnit),
        },
        Net_Sales_Qty: {
          FY2023: adjustValueByUnit(brand.Brands_GrandTotal?.Net_Sales_Qty?.FY2023 || 0, salesUnit),
          FY2024:adjustValueByUnit( brand.Brands_GrandTotal?.Net_Sales_Qty?.FY2024 || 0, salesUnit),
          FY2025:adjustValueByUnit( brand.Brands_GrandTotal?.Net_Sales_Qty?.FY2025 || 0, salesUnit),
        },
        Growth: {
          FY2023: brand.Brands_GrandTotal?.Growth?.FY2023 || 0,
          FY2024: brand.Brands_GrandTotal?.Growth?.FY2024 || 0,
          FY2025: brand.Brands_GrandTotal?.Growth?.FY2025 || 0,
        },
        Growth_Qty: {
          FY2023: brand.Brands_GrandTotal?.Growth_Qty?.FY2023 || 0,
          FY2024: brand.Brands_GrandTotal?.Growth_Qty?.FY2024 || 0,
          FY2025: brand.Brands_GrandTotal?.Growth_Qty?.FY2025 || 0,
        },
        PCPM: {
          FY2023: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM?.FY2025 || 0, salesUnit),
        },
        PCPM_Qty: {
          FY2023: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM_Qty?.FY2023 || 0, salesUnit),
          FY2024: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM_Qty?.FY2024 || 0, salesUnit),
          FY2025: adjustValueByUnit(brand.Brands_GrandTotal?.PCPM_Qty?.FY2025 || 0, salesUnit),
        },
        Per_Ach: {
          FY2023: brand.Brands_GrandTotal?.Per_Ach?.FY2023 || 0,
          FY2024: brand.Brands_GrandTotal?.Per_Ach?.FY2024 || 0,
          FY2025: brand.Brands_GrandTotal?.Per_Ach?.FY2025 || 0,
        },
        Per_Ach_Qty: {
          FY2023: brand.Brands_GrandTotal?.Per_Ach_Qty?.FY2023 || 0,
          FY2024: brand.Brands_GrandTotal?.Per_Ach_Qty?.FY2024 || 0,
          FY2025: brand.Brands_GrandTotal?.Per_Ach_Qty?.FY2025 || 0,
        },
      };

      return {
        ...brand,
        Brands_Months: brandMonths,
        Brands_Quarters: brandQuarters,
        Brands_GrandTotal: brandGrandTotal,
        Brands_Balance: adjustValueByUnit(brand.Brands_Balance || 0, salesUnit),
        Brands_Balance_Qty: adjustValueByUnit(brand.Brands_Balance_Qty || 0,salesUnit),
        SKUs: brand.SKUs.map((sku) => ensureCompleteData(sku, salesUnit)),
      };
    });

    return {
      ...brandFamily,
      BrandFamily_Months: brandFamilyMonths,
      BrandFamily_Quarters: brandFamilyQuarters,
      BrandFamily_GrandTotal: brandFamilyGrandTotal,
      BrandFamily_Balance: adjustValueByUnit(brandFamily.BrandFamily_Balance || 0, salesUnit),
      BrandFamily_Balance_Qty: adjustValueByUnit(brandFamily.BrandFamily_Balance_Qty || 0,salesUnit),
      Brands: normalizedBrands,
    };
  });
};

interface BrandTableProps {
  Fname: string;
}
const BrandTable: React.FC<BrandTableProps> = React.memo(({Fname}) => {

  const isBelow900px = useMediaQuery('(max-width:900px)');
  const { kpiData, salesUnit, loading, unit } = useSalesContext();
  const card3_data = kpiData?.kpi_data?.card4?.BrandFamilies;
  console.log(card3_data)

  const Quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const quarterMonths: { [key: string]: string[] } = {
    Q1: ['April', 'May', 'June'],
    Q2: ['July', 'August', 'September'],
    Q3: ['October', 'November', 'December'],
    Q4: ['January', 'February', 'March'],
  };
  const allMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March',
  ];
  const [selectedMonths, setSelectedMonths] = useState<string[]>(allMonths);
  const [showBrand, setShowBrand] = useState<boolean>(true);
  const [showSKU, setShowSKU] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'month' | 'quarter'>('month');
  const [expandedBrandFamilies, setExpandedBrandFamilies] = useState<string[]>([]);
  const [expandedBrands, setExpandedBrands] = useState<string[]>([]);


  const metrics = unit === 'Units'
    ? ['Net_Sales_Qty', 'Per_Ach_Qty', 'Growth_Qty', 'PCPM_Qty']
    : ['Net_Sales', 'Per_Ach', 'Growth', 'PCPM'];

  const data: BrandFamilyData[] = React.useMemo(() => {
    if (!card3_data || !Array.isArray(card3_data)) {
      return [];
    }
    return normalizeBrandAndFamilyData(card3_data, salesUnit);
  }, [card3_data, salesUnit]);

  useEffect(() => {
    if (data.length > 0) {
      setExpandedBrandFamilies(data.map(row => row.BrandFamily));
      setExpandedBrands(data.flatMap(row => row.Brands.map(brand => `${row.BrandFamily}-${brand.Name}`)));
    }
  }, [data]);
  
  const formatValue = (value: number, metric: string): string => {
    const numericValue = Number(value);
    const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                              (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
    if (isPercentageMetric) {
      return numericValue === 0 ? '0' : `${numericValue.toFixed(1)}%`; 
    }
    return numericValue === 0 ? '0' : numericValue.toFixed(1); 
  };
  const handleDownload = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData: any[] = [];

    const headers = [
      'Brand Family',
      ...(showBrand ? ['Brand'] : []),
      ...(showSKU ? ['SKU'] : []),
      '',
      'FY',
      ...(viewMode === 'month'
        ? Quarters.flatMap(quarter => [
            ...quarterMonths[quarter].filter(month => selectedMonths.includes(month)).map(month => `${quarter} ${month}`),
            `${quarter} Total`,
          ])
        : Quarters),
      'Grand Total',
      'Balance',
      'Balance Qty',
    ];
    worksheetData.push(headers);

    data.forEach((row) => {
      if (!showBrand) {
        metrics.forEach((metric, index) => {
          ['FY2023', 'FY2024', 'FY2025'].forEach(fy => {
            worksheetData.push([
              row.BrandFamily,
              '',
              '',
              index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM',
              fy,
              ...(viewMode === 'month'
                ? Quarters.flatMap(quarter => [
                    ...quarterMonths[quarter].filter(month => selectedMonths.includes(month)).map(month =>
                      formatValue(row.BrandFamily_Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0, metric) 
                    ),
                    formatValue(row.BrandFamily_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric), 
                  ])
                : Quarters.map(quarter =>
                    formatValue(row.BrandFamily_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric) 
                  )),
              formatValue(row.BrandFamily_GrandTotal?.[metric]?.[fy] ?? 0, metric), 
              metric === metrics[0] ? formatValue(row.BrandFamily_Balance ?? 0, metric) : '', 
              metric === metrics[0] ? formatValue(row.BrandFamily_Balance_Qty ?? 0, metric) : '', 
            ]);
          });
        });
      } else {
        row.Brands.forEach((brand) => {
          if (!showSKU) {
            metrics.forEach((metric, index) => {
              ['FY2023', 'FY2024', 'FY2025'].forEach(fy => {
                worksheetData.push([
                  row.BrandFamily,
                  brand.Name,
                  '',
                  index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM',
                  fy,
                  ...(viewMode === 'month'
                    ? Quarters.flatMap(quarter => [
                        ...quarterMonths[quarter].filter(month => selectedMonths.includes(month)).map(month =>
                          formatValue(brand.Brands_Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0, metric) 
                        ),
                        formatValue(brand.Brands_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric), 
                      ])
                    : Quarters.map(quarter =>
                        formatValue(brand.Brands_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric) 
                      )),
                  formatValue(brand.Brands_GrandTotal?.[metric]?.[fy] ?? 0, metric), 
                  metric === metrics[0] ? formatValue(brand.Brands_Balance ?? 0, metric) : '', 
                  metric === metrics[0] ? formatValue(brand.Brands_Balance_Qty ?? 0, metric) : '', 
                ]);
              });
            });
          } else {
            brand.SKUs.forEach((sku) => {
              metrics.forEach((metric, index) => {
                ['FY2023', 'FY2024', 'FY2025'].forEach(fy => {
                  worksheetData.push([
                    row.BrandFamily,
                    brand.Name,
                    sku.Name,
                    index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM',
                    fy,
                    ...(viewMode === 'month'
                      ? Quarters.flatMap(quarter => [
                          ...quarterMonths[quarter].filter(month => selectedMonths.includes(month)).map(month =>
                            formatValue(sku.Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0, metric) 
                          ),
                          formatValue(sku.Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric), 
                        ])
                      : Quarters.map(quarter =>
                          formatValue(sku.Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0, metric) 
                        )),
                    formatValue(sku.GrandTotal?.[metric]?.[fy] ?? 0, metric), 
                    metric === metrics[0] ? formatValue(sku.Balance ?? 0, metric) : '', 
                    metric === metrics[0] ? formatValue(sku.Balance_Qty ?? 0, metric) : '', 
                  ]);
                });
              });
            });
          }
        });
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Performance Data');

    const csvString = XLSX.utils.sheet_to_csv(worksheet);
    const base64Csv = btoa(unescape(encodeURIComponent(csvString)));
    const fileName = Fname;
  

    const isReactNativeWebView = !!window.ReactNativeWebView;
    const isInIframe = window.self !== window.top;
  
    if (isReactNativeWebView) {
  
      const message = {
        type: 'DOWNLOAD_CSV',
        csv: base64Csv,
        fileName:Fname,
        mimeType: 'text/csv',
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else if (isInIframe) {
      
      const message = {
        type: 'DOWNLOAD_CSV',
        csv: base64Csv,
        fileName:Fname,
        mimeType: 'text/csv',
      };
      window.parent.postMessage(JSON.stringify(message), 'https://bsvdashqa.mankindpharma.in');
    } else {
     
      XLSX.writeFile(workbook, fileName.replace('.csv', '.xlsx'));
      alert('File downloaded successfully!');
    }
  };

  const handleApplyMonths = (newSelectedMonths: string[]) => {
    setSelectedMonths(newSelectedMonths);
  };

  const handleBrandFamilyHeaderClick = () => {
    setShowBrand(prev => !prev);
    if (!showBrand) {
      setShowSKU(true);
    } else {
      setShowSKU(false);
    }
  };

  const handleBrandHeaderClick = () => {
    setShowSKU(prev => !prev);
  };

  const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViewMode(event.target.value as 'month' | 'quarter');
  };

  if (loading || !kpiData ) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  
  

  return (
    <div style={{ padding: "16px" }}>
      <Box className="header">
        <Box className="title-container">
          <Typography className="title">
          Brand Wise Sales ({salesUnit || "Lacs"})
          </Typography>
        </Box>
        <Box className="controls">
          <FormControl className="view-mode-control">
            <RadioGroup
              value={viewMode}
              onChange={handleViewChange}
              className="quaterly"
            >
              <FormControlLabel
                className="view-mode-label"
                value="quarter"
                control={<Radio className="custom-radio" />}
                label={<Typography className="view-mode-label">Quarterly</Typography>}
              />
              <FormControlLabel
                className="view-mode-label"
                value="month"
                control={<Radio className="custom-radio" />}
                label={<Typography className="view-mode-label">Monthly</Typography>}
              />
            </RadioGroup>
          </FormControl>
          {viewMode === 'month' && (
            <MonthSelectorDropdown
              months={allMonths}
              selectedMonths={selectedMonths}
              onApply={handleApplyMonths}
              label="Select Months"
            />
          )}
          <Button
            variant="contained"
            className="download-button"
            onClick={handleDownload}
          >
            Download MIS
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} className="table-container">
        <Table className="table" size="small">
          <TableHead>
            <TableRow className="header-row">
              
              <TableCell className="sticky-header zone-header cursor-pointer" rowSpan={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={handleBrandFamilyHeaderClick}>
                      <Typography className="header-text">
                      <b>Brand Family</b>
                      </Typography>
                      <Typography className="header-text ">
                        {!showBrand ? '−' : '+'}
                      </Typography>
                    </Box>
                  </TableCell>
              {showBrand && (
                <TableCell
                  sx={{ cursor: "pointer" }}
                  className="sticky-header region-header cursor-pointer"
                  
                  rowSpan={2}
                >
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={handleBrandHeaderClick}>
                          <Typography className="header-text " >
                          <b>Brand</b>
                          </Typography>
                          <Typography className="header-text ">
                            {showSKU ? '+' : '-'}
                          </Typography>
                        </Box>
                </TableCell>
              )}
              {showSKU && (
                <TableCell className={`sticky-header hq-header ${showBrand ? 'with-region' : ''}`} rowSpan={2}>
                  <b>SKU</b>
                </TableCell>
              )}
              <TableCell
                className={`sticky-header label-column  ${showBrand && showSKU ? 'both-visible' : (showBrand || showSKU ? 'one-visible' : 'newshift')}`}
                rowSpan={2}
              >
                <b></b>
              </TableCell>
              <TableCell
                className={`sticky-header label-column  ${showBrand && showSKU ? 'both-visible2' : (showBrand || showSKU ? 'one-visible2' : '')}`}
                rowSpan={2}
              >
                <b>FY</b>
              </TableCell>
              {viewMode === 'month'
                ? Quarters.map((quarter) => {
                    const monthsInQuarter = quarterMonths[quarter].filter((month) => selectedMonths.includes(month));
                    return monthsInQuarter.length > 0 ? (
                      <TableCell
                        key={quarter}
                        colSpan={monthsInQuarter.length + 1}
                        className="quater-header"
                        align="center"
                      >
                        <b>{quarter}</b>
                      </TableCell>
                    ) : null;
                  }).filter(Boolean)
                : Quarters.map((quarter) => (
                    <TableCell key={quarter} className="month-header" align="center">
                      <b>{quarter}</b>
                    </TableCell>
                  ))}
              <TableCell className="grand-total-header" rowSpan={2}>
                <b>Grand Total</b>
              </TableCell>
             
               {unit === 'Value' ? (
          <TableCell className="grand-total-header" rowSpan={2}>
            <b>Balance</b>
          </TableCell>
        ) : (
          <TableCell className="grand-total-header" rowSpan={2}>
            <b>Balance Qty</b>
          </TableCell>
        )}
            </TableRow>
            {viewMode === 'month' && (
              <TableRow className="header-row">
                {Quarters.map((quarter) => {
                  const monthsInQuarter = quarterMonths[quarter].filter((month) => selectedMonths.includes(month));
                  return monthsInQuarter.length > 0
                    ? monthsInQuarter.concat('Total').map((month) => (
                        <TableCell
                          key={`${quarter}-${month}`}
                          className="month-header"
                          align="center"
                          sx={{ top: "36px" }}
                        >
                          <b>{month}</b>
                        </TableCell>
                      ))
                    : null;
                }).filter(Boolean)}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {data.map((row, brandFamilyIndex) => (
              <React.Fragment key={`brandFamily-${brandFamilyIndex}`}>
                {expandedBrandFamilies.includes(row.BrandFamily) && (
                  <>
                    {!showBrand ? (
                      metrics.map((metric, index) => (
                        <React.Fragment key={metric}>
                          {['FY2023', 'FY2024', 'FY2025'].map((fy, fyIndex) => (
                            <TableRow key={`${metric}-${fy}`}>
                              {fyIndex === 0 && metric === metrics[0] && (
                                <TableCell
                                  rowSpan={12}
                                  className="sticky-body zone-cell"
                                >
                                  <Typography className="cell-text">
                                    <b>{row.BrandFamily || 'Null'}</b>
                                  </Typography>
                                </TableCell>
                              )}
                              {fyIndex === 0 && (
                                <TableCell
                                  rowSpan={3}
                                  className="sticky-body label-cell  newshift"
                                >
                                  <b>{index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM'}</b>
                                </TableCell>
                              )}
                              <TableCell className="sticky-body label-cell">
                                <b>FY{parseInt(fy.replace('FY', '')) - 1999}</b>
                              </TableCell>
                              {viewMode === 'month'
                                ? Quarters.map((quarter) => {
                                    const monthsInQuarter = quarterMonths[quarter].filter((month) => selectedMonths.includes(month));
                                    return monthsInQuarter.length > 0
                                      ? monthsInQuarter.concat('Total').map((month) => {
                                        const value = row.BrandFamily_Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0;
                                          const numericValue = Number(value);
                                          const displayValue = formatValue(value, metric); 
                                          const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                                    (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                          if (isPercentageMetric) {
                                            const isPositive = numericValue > 0;
                                            const isNegative = numericValue < 0;
                                            return (
                                         
                                              <TableCell
                                                key={`${quarter}-${month}`}
                                                className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''} ${month === 'Total' ? 'total-cells' : ''}`}
                                                align="center"
                                              >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  {isPositive ? (
                                                    
                                                    <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                  ) : isNegative ? (
                                                   <img src={ArrowDownwardIcon} alt="Down" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                  ) : null}
                                                  <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                    {displayValue}
                                                  </Typography>
                                                </Box>
                                              </TableCell>
                                            );
                                          }
                                          return (
                                            <TableCell
                                              key={`${quarter}-${month}`}
                                              className={`month-cell ${month === 'Total' ? 'total-cells' : ''}`}
                                              align="center"
                                            >
                                              {displayValue}
                                            </TableCell>
                                          );
                                        })
                                      : null;
                                  }).filter(Boolean)
                                : Quarters.map((quarter) => {
                                  const value = row.BrandFamily_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0;
                                  const numericValue = Number(value);
                                  const displayValue = formatValue(value, metric); 
                                  const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                            (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                  if (isPercentageMetric) {
                                    const isPositive = numericValue > 0;
                                    const isNegative = numericValue < 0;
                                    return (
                                    
                                        <TableCell
                                          key={quarter}
                                          className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''}`}
                                          align="center"
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {isPositive ? (
                                              <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                            ) : isNegative ? (
                                             <img src={ArrowDownwardIcon} alt="Down" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                            ) : null}
                                            <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                              {displayValue}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      );
                                    }
                                    return (
                                      <TableCell key={quarter} className="month-cell" align="center">
                                        {displayValue}
                                      </TableCell>
                                    );
                                  })}
                              <TableCell className="grand-total-cell">
                                {(() => {
                                  const value = row.BrandFamily_GrandTotal?.[metric]?.[fy] ?? 0;
                                  const numericValue = Number(value);
                                  const displayValue = formatValue(value, metric); 
                                  const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                            (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                  if (isPercentageMetric) {
                                    const isPositive = numericValue > 0;
                                    const isNegative = numericValue < 0;
                                    return (
                                 
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {isPositive ? (
                                          <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                        ) : isNegative ? (
                                         <img src={ArrowDownwardIcon} alt="Down" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                        ) : null}
                                        <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                          {displayValue}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  return displayValue;
                                })()}
                              </TableCell>
                              
                        {metric === metrics[0] && fyIndex === 0 && (
                          <TableCell rowSpan={12} className="balance-cell">
                            <Typography sx={{ fontSize: '0.9rem' }}>
                              {unit === 'Value'
                                ? formatValue(row.BrandFamily_Balance ?? 0, metric)
                                : formatValue(row.BrandFamily_Balance_Qty ?? 0, metric)}
                            </Typography>
                          </TableCell>
                        )}

                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      row.Brands.map((brand, brandIndex) => (
                        <React.Fragment key={`brand-${brandFamilyIndex}-${brandIndex}`}>
                          {expandedBrands.includes(`${row.BrandFamily}-${brand.Name}`) && (
                            <>
                              {!showSKU ? (
                                metrics.map((metric, index) => (
                                  <React.Fragment key={metric}>
                                    {['FY2023', 'FY2024', 'FY2025'].map((fy, fyIndex) => (
                                      <TableRow key={`${metric}-${fy}`}>
                                        {brandIndex === 0 && fyIndex === 0 && metric === metrics[0] && (
                                          <TableCell
                                            rowSpan={row.Brands.reduce((sum, b) => sum + (expandedBrands.includes(`${row.BrandFamily}-${b.Name}`) ? (showSKU ? b.SKUs.length * 12 : 12) : 0), 0)}
                                            className="sticky-body zone-cell"
                                          >
                                            <Typography className="cell-text">
                                              <b>{row.BrandFamily || 'Null'}</b>
                                            </Typography>
                                          </TableCell>
                                        )}
                                        {fyIndex === 0 && metric === metrics[0] && (
                                          <TableCell
                                            rowSpan={12}
                                            className="sticky-body region-cell"
                                          >
                                            <Typography className="cell-text">
                                              <b>{brand.Name || 'Null'}</b>
                                            </Typography>
                                          </TableCell>
                                        )}
                                        {fyIndex === 0 && (
                                          <TableCell
                                            rowSpan={3}
                                            className="sticky-body label-cell one-visible"
                                          >
                                            <b>{index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM'}</b>
                                          </TableCell>
                                        )}
                                        <TableCell className="sticky-body label-cell one-visible2">
                                          <b>FY{parseInt(fy.replace('FY', '')) - 1999}</b>
                                        </TableCell>
                                        {viewMode === 'month'
                                          ? Quarters.map((quarter) => {
                                            const monthsInQuarter = quarterMonths[quarter].filter((month) => selectedMonths.includes(month));
                                            return monthsInQuarter.length > 0
                                              ? monthsInQuarter.concat('Total').map((month) => {
                                                  const value = brand.Brands_Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0;
                                                  const numericValue = Number(value);
                                                  const displayValue = formatValue(value, metric); 
                                                  const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                                            (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                                  if (isPercentageMetric) {
                                                    const isPositive = numericValue > 0;
                                                    const isNegative = numericValue < 0;
                                                    return (
                                            
                                                        <TableCell
                                                          key={`${quarter}-${month}`}
                                                          className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''} ${month === 'Total' ? 'total-cells' : ''}`}
                                                          align="center"
                                                        >
                                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {isPositive ? (
                                                              <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                            ) : isNegative ? (
                                                              <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                            ) : null}
                                                            <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                              {displayValue}
                                                            </Typography>
                                                          </Box>
                                                        </TableCell>
                                                      );
                                                    }
                                                    return (
                                                      <TableCell
                                                        key={`${quarter}-${month}`}
                                                        className={`month-cell ${month === 'Total' ? 'total-cells' : ''}`}
                                                        align="center"
                                                      >
                                                        {displayValue}
                                                      </TableCell>
                                                    );
                                                  })
                                                : null;
                                            }).filter(Boolean)
                                          : Quarters.map((quarter) => {
                                             
                                                const value = brand.Brands_Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0;
                                                const numericValue = Number(value);
                                                const displayValue = formatValue(value, metric); 
                                                const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                                          (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                                if (isPercentageMetric) {
                                                  const isPositive = numericValue > 0;
                                                  const isNegative = numericValue < 0;
                                                  return (
                                                  <TableCell
                                                    key={quarter}
                                                    className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''}`}
                                                    align="center"
                                                  >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      {isPositive ? (
                                                        <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                      ) : isNegative ? (
                                                        <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                      ) : null}
                                                      <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                        {displayValue}
                                                      </Typography>
                                                    </Box>
                                                  </TableCell>
                                                );
                                              }
                                              return (
                                                <TableCell key={quarter} className="month-cell" align="center">
                                                  {displayValue}
                                                </TableCell>
                                              );
                                            })}
                                        <TableCell className="grand-total-cell">
                                          {(() => {
                                          
                                              const value = brand.Brands_GrandTotal?.[metric]?.[fy] ?? 0;
                                              const numericValue = Number(value);
                                              const displayValue = formatValue(value, metric); 
                                              const isPercentageMetric = (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                                        (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                              if (isPercentageMetric) {
                                                const isPositive = numericValue > 0;
                                                const isNegative = numericValue < 0;
                                                return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  {isPositive ? (
                                                    <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                  ) : isNegative ? (
                                                    <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                  ) : null}
                                                  <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                    {displayValue}
                                                  </Typography>
                                                </Box>
                                              );
                                            }
                                            return displayValue;
                                          })()}
                                        </TableCell>
                                       
                                        {metric === metrics[0] && fyIndex === 0 && (
  <TableCell rowSpan={12} className="balance-cell">
    <Typography sx={{ fontSize: '0.9rem' }}>
      {unit === 'Value'
        ? formatValue(brand.Brands_Balance ?? 0, metric)
        : formatValue(brand.Brands_Balance_Qty ?? 0, metric)}
    </Typography>
  </TableCell>
)}
                                      </TableRow>
                                    ))}
                                  </React.Fragment>
                                ))
                              ) : (
                                brand.SKUs.map((sku, skuIndex) => (
                                  <React.Fragment key={`sku-${brandFamilyIndex}-${brandIndex}-${skuIndex}`}>
                                    {metrics.map((metric, index) => (
                                      <React.Fragment key={metric}>
                                        {['FY2023', 'FY2024', 'FY2025'].map((fy, fyIndex) => (
                                          <TableRow key={`${metric}-${fy}`}>
                                            {skuIndex === 0 && brandIndex === 0 && fyIndex === 0 && metric === metrics[0] && (
                                              <TableCell
                                                rowSpan={row.Brands.reduce((sum, b) => sum + (expandedBrands.includes(`${row.BrandFamily}-${b.Name}`) ? b.SKUs.length * 12 : 0), 0)}
                                                className="sticky-body zone-cell"
                                              >
                                                <Typography className="cell-text">
                                                  <b>{row.BrandFamily || 'Null'}</b>
                                                </Typography>
                                              </TableCell>
                                            )}
                                            {skuIndex === 0 && fyIndex === 0 && metric === metrics[0] && (
                                              <TableCell
                                                rowSpan={brand.SKUs.length * 12}
                                                className="sticky-body region-cell"
                                              >
                                                <Typography className="cell-text">
                                                  <b>{brand.Name || 'Null'}</b>
                                                </Typography>
                                              </TableCell>
                                            )}
                                            {fyIndex === 0 && metric === metrics[0] && (
                                              <TableCell rowSpan={12} className="sticky-body hq-cell with-region">
                                                <Typography className="cell-text">
                                                  <b>{sku.Name || 'Null'}</b>
                                                </Typography>
                                              </TableCell>
                                            )}
                                            {fyIndex === 0 && (
                                              <TableCell
                                                rowSpan={3}
                                                className="sticky-body label-cell both-visible"
                                              >
                                                <b>{index === 0 ? 'Sales' : index === 1 ? '%Ach' : index === 2 ? 'Grw' : 'PCPM'}</b>
                                              </TableCell>
                                            )}
                                            <TableCell className="sticky-body label-cell both-visible2">
                                              <b>FY{parseInt(fy.replace('FY', '')) - 1999}</b>
                                            </TableCell>
                                            
                                                          {viewMode === 'month'
              ? Quarters.map((quarter) => {
                  const monthsInQuarter = quarterMonths[quarter].filter((month) => selectedMonths.includes(month));
                  return monthsInQuarter.length > 0
                    ? monthsInQuarter.concat('Total').map((month) => {
                        const value = sku.Quarters[quarter]?.Months[month]?.[metric]?.[fy] ?? 0;
                        
                        const numericValue = Number(value);
                        const displayValue = formatValue(value, metric); 
                        const isPercentageMetric =
                          (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                          (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth')); 
                        if (isPercentageMetric) {
                          const isPositive = numericValue > 0;
                          const isNegative = numericValue < 0;
                          return (
                                                            <TableCell
                                                              key={`${quarter}-${month}`}
                                                              className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''} ${month === 'Total' ? 'total-cells' : ''}`}
                                                              align="center"
                                                            >
                                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {isPositive ? (
                                                                  <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                                ) : isNegative ? (
                                                                  <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                                ) : null}
                                                                <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                                  {displayValue}
                                                                </Typography>
                                                              </Box>
                                                            </TableCell>
                                                          );
                                                        }
                                                        return (
                                                          <TableCell
                                                            key={`${quarter}-${month}`}
                                                            className={`month-cell ${month === 'Total' ? 'total-cells' : ''}`}
                                                            align="center"
                                                          >
                                                            {displayValue}
                                                          </TableCell>
                                                        );
                                                      })
                                                    : null;
                                                }).filter(Boolean)
                                              : Quarters.map((quarter) => {
                                                const value = sku.Quarters[quarter]?.Months.Total?.[metric]?.[fy] ?? 0;
                                                
                                                const numericValue = Number(value);
                                                const displayValue = formatValue(value, metric); 
                                                const isPercentageMetric =
                                                  (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                  (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth')); 
                                                if (isPercentageMetric) {
                                                  const isPositive = numericValue > 0;
                                                  const isNegative = numericValue < 0;
                                                  return (
                                                      <TableCell
                                                        key={quarter}
                                                        className={`month-cell growth-cell ${isNegative ? 'negative' : isPositive ? 'positive' : ''}`}
                                                        align="center"
                                                      >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                          {isPositive ? (
                                                            <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                          ) : isNegative ? (
                                                            <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                          ) : null}
                                                          <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                            {displayValue}
                                                          </Typography>
                                                        </Box>
                                                      </TableCell>
                                                    );
                                                  }
                                                  return (
                                                    <TableCell key={quarter} className="month-cell" align="center">
                                                      {displayValue}
                                                    </TableCell>
                                                  );
                                                })}
                                            <TableCell className="grand-total-cell">
                                              {(() => {
                                                 const value = sku.GrandTotal?.[metric]?.[fy] ?? 0;
              
                                                 const numericValue = Number(value);
                                                 const displayValue = formatValue(value, metric); 
                                                 const isPercentageMetric =
                                                   (unit === 'Units' && (metric === 'Per_Ach_Qty' || metric === 'Growth_Qty')) ||
                                                   (unit !== 'Units' && (metric === 'Per_Ach' || metric === 'Growth'));
                                                 if (isPercentageMetric) {
                                                   const isPositive = numericValue > 0;
                                                   const isNegative = numericValue < 0;
                                                   return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      {isPositive ? (
                                                        <img src={ArrowUpwardIcon} alt="Up Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                      ) : isNegative ? (
                                                        <img src={ArrowDownwardIcon} alt="Down Arrow" style={{ width: isBelow900px ? 12 : 16, marginRight: isBelow900px ? 3 : 4 }} />
                                                      ) : null}
                                                      <Typography sx={{ color: isNegative ? 'red' : isPositive ? 'green' : 'inherit' , fontSize: isBelow900px ? "10px" : "14px", }}>
                                                        {displayValue}
                                                      </Typography>
                                                    </Box>
                                                  );
                                                }
                                                return displayValue;
                                              })()}
                                            </TableCell>
                                       
                                      {metric === metrics[0] && fyIndex === 0 && (
                                        <TableCell rowSpan={12} className="balance-cell">
                                          <Typography sx={{ fontSize: '0.9rem' }}>
                                            {unit === 'Value'
                                              ? formatValue(sku.Balance ?? 0, metric)
                                              : formatValue(sku.Balance_Qty ?? 0, metric)}
                                          </Typography>
                                        </TableCell>
                                      )}
                                          </TableRow>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </React.Fragment>
                                ))
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

export default BrandTable;